import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 32px;
  
  h1 {
    font-size: 1.75rem;
    color: #1a1a2e;
    margin-bottom: 8px;
  }
  
  p {
    color: #666;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  
  ${props => props.highlight && `
    background: linear-gradient(135deg, #1976d2, #1565c0);
    color: white;
    
    h3 { color: rgba(255,255,255,0.8); }
    .value { color: white; }
  `}
  
  ${props => props.warning && `
    border-left: 4px solid #FF9800;
  `}
  
  h3 {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 8px;
    text-transform: uppercase;
  }
  
  .value {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1a1a2e;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 24px;
  
  h2 {
    font-size: 1.25rem;
    margin-bottom: 20px;
    color: #1a1a2e;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 14px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background: #f9f9f9;
    font-weight: 600;
    color: #666;
    font-size: 0.85rem;
  }
  
  tr:hover {
    background: #fafafa;
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  
  ${props => props.primary && `
    background: #4CAF50;
    color: white;
    &:hover { background: #43A047; }
  `}
  
  ${props => props.secondary && `
    background: #f5f5f5;
    color: #333;
    border: 1px solid #ddd;
    &:hover { background: #eee; }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  padding: 32px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h2 { font-size: 1.25rem; }
  
  button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #333;
  }
  
  input, select {
    width: 100%;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #1976d2;
    }
  }
`;

const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'completed': return '#e8f5e9';
      case 'processing': return '#fff3e0';
      case 'pending': return '#e3f2fd';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'completed': return '#2e7d32';
      case 'processing': return '#f57c00';
      case 'pending': return '#1976d2';
      default: return '#666';
    }
  }};
`;

const formatCurrency = (amount) => {
  return `GHS ${parseFloat(amount || 0).toFixed(2)}`;
};

const Payouts = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showMomoModal, setShowMomoModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    paymentMethod: 'manual',
    paymentReference: '',
    notes: ''
  });
  
  const [momoForm, setMomoForm] = useState({
    momoNumber: '',
    momoProvider: 'mtn',
    accountName: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [financesRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/admin/finances`),
        axios.get(`${API_URL}/admin/payouts/history`)
      ]);
      
      setSummary(financesRes.data.summary);
      setPendingPayouts(financesRes.data.pendingPayouts);
      setPayoutHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  const openPayoutModal = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setPayoutForm({
      amount: practitioner.pendingEarnings,
      paymentMethod: 'manual',
      paymentReference: '',
      notes: ''
    });
    setShowPayoutModal(true);
  };

  const openMomoModal = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setMomoForm({
      momoNumber: '',
      momoProvider: 'mtn',
      accountName: `${practitioner.firstName} ${practitioner.lastName}`
    });
    setShowMomoModal(true);
  };

  const handleManualPayout = async () => {
    try {
      setProcessing(true);
      
      await axios.post(`${API_URL}/admin/payouts/process/${selectedPractitioner.id}`, {
        amount: parseFloat(payoutForm.amount),
        paymentMethod: payoutForm.paymentMethod,
        paymentReference: payoutForm.paymentReference,
        notes: payoutForm.notes
      });
      
      toast.success('Payout processed successfully!');
      setShowPayoutModal(false);
      loadData();
    } catch (error) {
      console.error('Payout error:', error);
      toast.error(error.response?.data?.error || 'Failed to process payout');
    } finally {
      setProcessing(false);
    }
  };

  const handleMomoTransfer = async () => {
    try {
      setProcessing(true);
      
      // First create recipient
      const recipientRes = await axios.post(`${API_URL}/admin/payouts/create-recipient`, {
        practitionerId: selectedPractitioner.id,
        momoNumber: momoForm.momoNumber,
        momoProvider: momoForm.momoProvider,
        accountName: momoForm.accountName
      });
      
      // Then initiate transfer
      await axios.post(`${API_URL}/admin/payouts/transfer/${selectedPractitioner.id}`, {
        recipientCode: recipientRes.data.recipientCode,
        amount: parseFloat(selectedPractitioner.pendingEarnings)
      });
      
      toast.success('Transfer initiated successfully!');
      setShowMomoModal(false);
      loadData();
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate transfer');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container>
      <Header>
        <h1>💰 Payouts Management</h1>
        <p>Manage practitioner earnings and process payouts</p>
      </Header>
      
      <StatsGrid>
        <StatCard>
          <h3>Total Revenue</h3>
          <div className="value">{formatCurrency(summary.totalRevenue)}</div>
        </StatCard>
        <StatCard highlight>
          <h3>Admin Earnings (80%)</h3>
          <div className="value">{formatCurrency(summary.adminEarnings)}</div>
        </StatCard>
        <StatCard warning>
          <h3>Pending Payouts</h3>
          <div className="value">{formatCurrency(summary.pendingPayouts)}</div>
        </StatCard>
        <StatCard>
          <h3>Total Paid Out</h3>
          <div className="value">{formatCurrency(summary.paidOut)}</div>
        </StatCard>
      </StatsGrid>
      
      <Section>
        <h2>⏳ Pending Payouts to Practitioners (20% Share)</h2>
        {pendingPayouts.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>Practitioner</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Pending Amount</th>
                <th>Total Earned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingPayouts.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.firstName} {p.lastName}</strong></td>
                  <td>{p.email}</td>
                  <td>{p.phone || '-'}</td>
                  <td style={{ fontWeight: 'bold', color: '#f57c00' }}>
                    {formatCurrency(p.pendingEarnings)}
                  </td>
                  <td>{formatCurrency(p.totalEarnings)}</td>
                  <td>
                    <Button primary onClick={() => openPayoutModal(p)} style={{ marginRight: '8px' }}>
                      💵 Mark Paid
                    </Button>
                    <Button secondary onClick={() => openMomoModal(p)}>
                      📱 Pay via MoMo
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
            No pending payouts at this time.
          </p>
        )}
      </Section>
      
      <Section>
        <h2>📜 Payout History</h2>
        {payoutHistory.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Practitioner</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Processed By</th>
              </tr>
            </thead>
            <tbody>
              {payoutHistory.map(w => (
                <tr key={w.id}>
                  <td>{new Date(w.createdAt).toLocaleDateString()}</td>
                  <td>{w.Practitioner?.firstName} {w.Practitioner?.lastName}</td>
                  <td>{formatCurrency(w.amount)}</td>
                  <td>{w.paymentMethod}</td>
                  <td><Badge status={w.status}>{w.status}</Badge></td>
                  <td>{w.processor?.firstName} {w.processor?.lastName}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
            No payout history yet.
          </p>
        )}
      </Section>
      
      {/* Manual Payout Modal */}
      {showPayoutModal && selectedPractitioner && (
        <Modal onClick={() => setShowPayoutModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h2>Process Payout</h2>
              <button onClick={() => setShowPayoutModal(false)}>×</button>
            </ModalHeader>
            
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Processing payout for <strong>{selectedPractitioner.firstName} {selectedPractitioner.lastName}</strong>
            </p>
            
            <FormGroup>
              <label>Amount (GHS)</label>
              <input
                type="number"
                step="0.01"
                value={payoutForm.amount}
                onChange={e => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                max={selectedPractitioner.pendingEarnings}
              />
              <small style={{ color: '#888' }}>
                Max: {formatCurrency(selectedPractitioner.pendingEarnings)}
              </small>
            </FormGroup>
            
            <FormGroup>
              <label>Payment Method</label>
              <select
                value={payoutForm.paymentMethod}
                onChange={e => setPayoutForm({ ...payoutForm, paymentMethod: e.target.value })}
              >
                <option value="manual">Manual Transfer</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="cash">Cash</option>
              </select>
            </FormGroup>
            
            <FormGroup>
              <label>Reference/Transaction ID</label>
              <input
                type="text"
                value={payoutForm.paymentReference}
                onChange={e => setPayoutForm({ ...payoutForm, paymentReference: e.target.value })}
                placeholder="e.g., TXN123456"
              />
            </FormGroup>
            
            <FormGroup>
              <label>Notes (Optional)</label>
              <input
                type="text"
                value={payoutForm.notes}
                onChange={e => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                placeholder="Any additional notes"
              />
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <Button secondary onClick={() => setShowPayoutModal(false)} style={{ flex: 1 }}>
                Cancel
              </Button>
              <Button primary onClick={handleManualPayout} disabled={processing} style={{ flex: 1 }}>
                {processing ? 'Processing...' : 'Confirm Payout'}
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
      
      {/* MoMo Transfer Modal */}
      {showMomoModal && selectedPractitioner && (
        <Modal onClick={() => setShowMomoModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h2>📱 Mobile Money Transfer</h2>
              <button onClick={() => setShowMomoModal(false)}>×</button>
            </ModalHeader>
            
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Send {formatCurrency(selectedPractitioner.pendingEarnings)} to{' '}
              <strong>{selectedPractitioner.firstName} {selectedPractitioner.lastName}</strong>
            </p>
            
            <FormGroup>
              <label>Mobile Money Provider</label>
              <select
                value={momoForm.momoProvider}
                onChange={e => setMomoForm({ ...momoForm, momoProvider: e.target.value })}
              >
                <option value="mtn">MTN Mobile Money</option>
                <option value="vodafone">Vodafone Cash</option>
                <option value="airteltigo">AirtelTigo Money</option>
              </select>
            </FormGroup>
            
            <FormGroup>
              <label>Mobile Number</label>
              <input
                type="tel"
                value={momoForm.momoNumber}
                onChange={e => setMomoForm({ ...momoForm, momoNumber: e.target.value })}
                placeholder="0241234567"
              />
            </FormGroup>
            
            <FormGroup>
              <label>Account Name</label>
              <input
                type="text"
                value={momoForm.accountName}
                onChange={e => setMomoForm({ ...momoForm, accountName: e.target.value })}
              />
            </FormGroup>
            
            <div style={{ 
              background: '#FFF8E1', 
              padding: '12px', 
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              color: '#5D4037'
            }}>
              ⚠️ This will initiate a real transfer via Paystack. Make sure the details are correct.
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button secondary onClick={() => setShowMomoModal(false)} style={{ flex: 1 }}>
                Cancel
              </Button>
              <Button primary onClick={handleMomoTransfer} disabled={processing} style={{ flex: 1 }}>
                {processing ? 'Processing...' : 'Send Payment'}
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Payouts;
